/**
 * useThemeV2 - Hook for fetching a single V2 theme by ID
 *
 * Provides loading state, error handling, and refresh functionality.
 * Uses ThemeV2Service (themesV2/ collection).
 */

import { useState, useEffect, useCallback } from 'react';
import { ThemeV2Service } from '@modules/firebase-core/services/ThemeV2Service';
import type { ThemeV2 } from '../types/themesV2.types';

export interface UseThemeV2Return {
  /** Theme data (null if not loaded or not found) */
  theme: ThemeV2 | null;
  /** Whether theme is being loaded */
  loading: boolean;
  /** Error if fetch failed or theme not found */
  error: Error | null;
  /** Refresh the theme data */
  refresh: () => void;
}

/**
 * useThemeV2 - Fetch a single V2 theme by ID
 */
export function useThemeV2(themeId: string): UseThemeV2Return {
  const [theme, setTheme] = useState<ThemeV2 | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const themeService = new ThemeV2Service();

  const fetchTheme = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const fetchedTheme = await themeService.get(themeId);

      if (!fetchedTheme) {
        setError(new Error('V2 Theme not found'));
        setTheme(null);
      } else {
        setTheme(fetchedTheme);
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch V2 theme'));
      setTheme(null);
    } finally {
      setLoading(false);
    }
  }, [themeId]);

  useEffect(() => {
    fetchTheme();
  }, [fetchTheme]);

  const refresh = useCallback(() => {
    fetchTheme();
  }, [fetchTheme]);

  return {
    theme,
    loading,
    error,
    refresh,
  };
}

export default useThemeV2;
